import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getTranslations,
  decodeName,
  playerPoints,
  splitName,
  getBoosterCategory,
} from "../../Common/utils";
import TeamSerialNumber from "../TeamSerialNumber";
import { PlayerMiniCard } from "../PlayerMiniCard";
import { PlayerBudget } from "../PlayerBudget";
import { BoosterChips } from "../BoosterChips";
import { TotalPointsSection } from "../TotalPointsSection";
import ListViewImage from "../Cloudinary/ListViewImage";
import useCloudinaryData from "../../Common/hooks/useCloudinaryData";
import SectionLoader from "../Loaders/SectionLoader";
import { NoDataFound } from "../NoDataFound";
import StatisticsImage from "../Cloudinary/StatisticsImage";
import { useSelector } from "react-redux";
import RaceweekFlag from "../Cloudinary/RaceweekFlag";
import Notes from "../Notes";
import { PlayerInfo } from "../PlayerInfo";
import { gameplay } from "../../redux/actions";
import { ProvisionalPtsTooltip } from "../ProvisionalPtsTooltip";

const getPlayerBoosterChips = (player) => {
  const chipIds = [];

  if (player?.isCaptain === true) {
    chipIds.push("captain");
  }

  if (player?.isExtraDRS === true) {
    chipIds.push("extraDRS");
  }

  if (player?.IsActive === 0 || player?.IsActive === "0") {
    chipIds.push("inactive");
  }

  if (player?.isFinalFixNewPlayer === true) {
    chipIds.push("finalFix");
  }

  return chipIds.map((chipId, index) => {
    const position = chipIds.length === 1 ? 0 : index === 0 ? 0 : 1;
    return { chipId, position };
  });
};

const PlayerImageWithChips = ({ player }) => {
  const boosterChips = getPlayerBoosterChips(player);
  const hasMultipleChips = boosterChips.length > 1;

  return (
    <div className="si-oppositeTeamView__roster-image-wrapper">
      <div className="si-oppositeTeamView__roster-image">
        <StatisticsImage statisticsPlayer={player} />
      </div>
      {boosterChips.length > 0 &&
        boosterChips.map((chip, index) => {
          return (
            <div
              key={`${chip.chipId}-${index}`}
              className={`si-oppositeTeamView__roster-chip si-oppositeTeamView__roster-chip--${chip.position} ${
                hasMultipleChips
                  ? "si-oppositeTeamView__roster-chip--multiple"
                  : ""
              }`}
            >
              {chip.chipId === "finalFix" && (
                <BoosterChips
                  {...{
                    boosterCls: getBoosterCategory("finalFix")?.boosterCls,
                    boosterIcon: getBoosterCategory("finalFix")?.boosterIcon,
                  }}
                />
              )}
              {chip.chipId === "extraDRS" && (
                <BoosterChips
                  {...{
                    boosterCls: getBoosterCategory("extraDRS")?.boosterCls,
                    boosterIcon: getBoosterCategory("extraDRS")?.boosterIcon,
                  }}
                />
              )}
              {chip.chipId === "captain" && (
                <div className="si-booster__box si-captain">
                  <div className="si-booster__box-icon">
                    <span>2X</span>
                  </div>
                </div>
              )}
              {chip.chipId === "inactive" && (
                <div className="si-booster__box si-not-playing">
                  <div className="si-booster__box-icon">
                    <i className="f1i-not-playing"></i>
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

const OppositeTeamView = ({
  selectedMember = null,
  oppositeTeamDetails = null,
  selectedOpponentBooster = null,
  fetchOppDetailedTeamLoading = false,
  meetingFixtures = null,
  selectedOption = null,
  userSocialId = null,
  leagueSelectField = null,
  oppUserGameDays = null,
  showOnlyMyTeams = false,
  isProvisional = false,
}) => {
  const dispatch = useDispatch();
  const fixtureCircuitConfigState = useSelector(
    (state) => state.fixtureCircuitConfig
  );
  const fixtures = fixtureCircuitConfigState?.data?.fixtures;

  const [isFlagSquareImg, setIsFlagSquareImg] = useState(false);

  const handlePlayerClick = (player) => {
    dispatch(gameplay.togglePlayerPopup(player));
  };

  function updateFlagSquareImg(value) {
    setIsFlagSquareImg(value);
  }

  const leagueCardThumbStyle = isFlagSquareImg ? { borderRadius: "0px" } : {};

  const selectedFixture = useMemo(() => {
    let currentGamedayId =
      selectedOption?.id === 0
        ? leagueSelectField?.[leagueSelectField?.length - 1]?.id
        : selectedOption?.id;

    return fixtures?.find((fixture) => fixture.GamedayId === currentGamedayId);
  }, [fixtures, selectedOption, leagueSelectField]);

  const { cloudinaryBase, envVersion, currentSeasonYear, configData } =
    useCloudinaryData();

  const data = useMemo(() => {
    const calculatePoints = () => {
      if (selectedOption?.id === 0) {
        if (oppUserGameDays?.mdDetails) {
          const gamedayIds = Object.keys(oppUserGameDays.mdDetails)
            .map((key) => Number(key))
            .filter((id) => !isNaN(id))
            .sort((a, b) => b - a);

          if (gamedayIds.length > 0) {
            const latestGamedayId = gamedayIds[0];
            const latestMdDetailsEntry =
              oppUserGameDays.mdDetails[latestGamedayId];

            if (
              latestMdDetailsEntry &&
              latestMdDetailsEntry.pts !== undefined
            ) {
              return latestMdDetailsEntry.pts;
            }
          }
        }
        return oppositeTeamDetails?.ovpoints ?? null;
      }

      if (selectedOption?.id && oppUserGameDays?.mdDetails) {
        const gamedayId = Number(selectedOption.id);
        const mdDetailsEntry = oppUserGameDays.mdDetails[gamedayId];

        if (mdDetailsEntry && mdDetailsEntry.pts !== undefined) {
          return mdDetailsEntry.pts;
        }

        const stringKey = String(selectedOption.id);
        const stringMdDetailsEntry = oppUserGameDays.mdDetails[stringKey];

        if (stringMdDetailsEntry && stringMdDetailsEntry.pts !== undefined) {
          return stringMdDetailsEntry.pts;
        }
      }

      return (
        oppositeTeamDetails?.ovpoints ??
        selectedMember?.cur_points ??
        selectedMember?.points ??
        null
      );
    };

    if (oppositeTeamDetails) {
      const firstDriver = oppositeTeamDetails?.team?.find(
        (p) => p.PositionName === "DRIVER"
      );
      const countryName =
        firstDriver?.CountryName ??
        oppositeTeamDetails?.team?.[0]?.CountryName ??
        selectedMember?.countryName ??
        null;
      const countryCode =
        firstDriver?.CountryCode?.toLowerCase() ||
        oppositeTeamDetails?.team?.[0]?.CountryCode?.toLowerCase() ||
        selectedMember?.countryCode ||
        null;

      const roundNo = meetingFixtures?.[0]?.MeetingNumber
        ? `R${String(meetingFixtures[0].MeetingNumber).padStart(2, "0")}`
        : selectedMember?.roundNo || null;

      const booster = selectedOpponentBooster?.[0] || null;

      const players =
        oppositeTeamDetails?.team?.map((player) => {
          const points = playerPoints({
            player,
            team: oppositeTeamDetails,
            oppDetailedTeams: [oppositeTeamDetails],
          });

          const isFinalFixReplaced =
            oppositeTeamDetails?.boosterid === 3 &&
            (player?.isFinalFixOldPlayer || false);
          const isSwapped =
            player?.isSwappedOutPlayer || player?.isSwappedInPlayer || false;
          const isReplaced = isFinalFixReplaced || isSwapped;

          return {
            ...player,
            points: points !== "-" ? Number(points) : 0,
            isReplaced,
            isFinalFixReplaced,
            isSwapped,
            isFinalFixNewPlayer:
              oppositeTeamDetails?.boosterid === 3 &&
              (player?.isFinalFixNewPlayer || false),
            hasVideo: false,
          };
        }) || [];

      return {
        teamNo:
          oppositeTeamDetails?.teamno ??
          selectedMember?.team_no ??
          selectedMember?.teamNo ??
          null,
        teamName:
          oppositeTeamDetails?.teamname ??
          (decodeName(
            decodeName(
              selectedMember?.team_name ?? selectedMember?.teamName ?? ""
            )
          ) ||
            null) ??
          null,
        userName:
          (decodeName(
            decodeName(
              selectedMember?.user_name ?? selectedMember?.userName ?? ""
            )
          ) ||
            null) ??
          null,
        countryName,
        countryCode,
        roundNo,
        costCap: oppositeTeamDetails?.maxteambal
          ? Number((oppositeTeamDetails.maxteambal / 1000000).toFixed(1))
          : null,
        booster,
        points: calculatePoints(),
        players,
        social_id:
          oppositeTeamDetails?.social_id ??
          oppositeTeamDetails?.user_guid ??
          selectedMember?.social_id ??
          null,
      };
    }

    if (!selectedMember) {
      return null;
    }

    const calculatePointsForFallback = () => {
      if (selectedOption?.id === 0) {
        if (oppUserGameDays?.mdDetails) {
          const gamedayIds = Object.keys(oppUserGameDays.mdDetails)
            .map((key) => Number(key))
            .filter((id) => !isNaN(id))
            .sort((a, b) => b - a);

          if (gamedayIds.length > 0) {
            const latestGamedayId = gamedayIds[0];
            const latestMdDetailsEntry =
              oppUserGameDays.mdDetails[latestGamedayId];

            if (
              latestMdDetailsEntry &&
              latestMdDetailsEntry.pts !== undefined
            ) {
              return latestMdDetailsEntry.pts;
            }
          }
        }
        return (
          oppositeTeamDetails?.ovpoints ??
          selectedMember.cur_points ??
          selectedMember.points ??
          null
        );
      }

      if (selectedOption?.id && oppUserGameDays?.mdDetails) {
        const gamedayId = Number(selectedOption.id);
        const mdDetailsEntry = oppUserGameDays.mdDetails[gamedayId];

        if (mdDetailsEntry && mdDetailsEntry.pts !== undefined) {
          return mdDetailsEntry.pts;
        }

        const stringKey = String(selectedOption.id);
        const stringMdDetailsEntry = oppUserGameDays.mdDetails[stringKey];

        if (stringMdDetailsEntry && stringMdDetailsEntry.pts !== undefined) {
          return stringMdDetailsEntry.pts;
        }
      }

      return (
        oppositeTeamDetails?.ovpoints ??
        selectedMember.cur_points ??
        selectedMember.points ??
        null
      );
    };

    return {
      teamNo: selectedMember.team_no ?? selectedMember.teamNo ?? null,
      teamName:
        decodeName(
          decodeName(selectedMember.team_name ?? selectedMember.teamName ?? "")
        ) || null,
      userName:
        decodeName(
          decodeName(selectedMember.user_name ?? selectedMember.userName ?? "")
        ) || null,
      countryName: selectedMember.countryName || null,
      countryCode: selectedMember.countryCode || null,
      roundNo: selectedMember.roundNo || null,
      costCap: selectedMember.costCap || null,
      booster: selectedMember.booster || null,
      points: calculatePointsForFallback(),
      players: selectedMember.players || [],

      cur_rank: selectedMember.cur_rank,
      trend: selectedMember.trend,
      social_id: selectedMember.social_id,
      user_team: selectedMember.user_team,
    };
  }, [
    selectedMember,
    oppositeTeamDetails,
    selectedOpponentBooster,
    meetingFixtures,
    selectedOption,
    oppUserGameDays,
  ]);

  const isRowDisabled = useMemo(() => {
    if (!selectedMember) return false;

    const rank = selectedMember.cur_rank ?? selectedMember.rank;
    const isRankDisabled = isNaN(rank) || rank === undefined || rank === null;

    const points = selectedMember.cur_points ?? selectedMember.points;
    const isPointsDisabled =
      isNaN(points) || points === undefined || points === null;

    return isRankDisabled || isPointsDisabled;
  }, [selectedMember]);

  if (showOnlyMyTeams && isRowDisabled && !oppositeTeamDetails) {
    return (
      <div className="si-oppositeTeamView si-oppositeTeamView--empty">
        <NoDataFound
          noDataCaption={getTranslations(
            "opponent_no_data_found_title",
            "No opponent team found"
          )}
        />
      </div>
    );
  }

  // Show empty state when no data is available
  if (!data || (!data.teamName && !data.userName && !data.players?.length)) {
    return (
      <div className="si-oppositeTeamView si-oppositeTeamView--empty">
        <NoDataFound
          noDataCaption={getTranslations(
            "opponent_no_data_found_title",
            "No opponent team found"
          )}
        />
      </div>
    );
  }

  const drivers = data.players
    .filter((player) => player.PositionName === "DRIVER")
    .sort((a, b) => {
      const aIsCaptain = a?.isCaptain === true;
      const bIsCaptain = b?.isCaptain === true;
      if (aIsCaptain && !bIsCaptain) return -1;
      if (!aIsCaptain && bIsCaptain) return 1;
      return 0;
    });
  const constructors = data.players.filter(
    (player) => player.PositionName === "CONSTRUCTOR"
  );

  // Determine if this is the user's team
  const isUserTeam =
    userSocialId &&
    data.social_id &&
    String(userSocialId) === String(data.social_id);

  const flagImagePath = configData?.imagePaths?.flagImg
    ?.replaceAll("<cloudinary_base_path>", cloudinaryBase)
    ?.replaceAll("<env_version>", envVersion)
    ?.replaceAll("<current_season_year>", currentSeasonYear)
    ?.replaceAll("<country_short_name>", data.countryCode || "esp");

  const formatPoints = (points) => {
    if (points === null || points === undefined) return "00";
    if (points < 0) return `-${Math.abs(points).toString().padStart(2, "0")}`;
    return points.toString().padStart(2, "0");
  };

  // Show loader while fetching data
  if (fetchOppDetailedTeamLoading) {
    return (
      <div className="si-oppositeTeamView">
        <div className="si-main__container">
          <NoDataFound
            noDataCaption={getTranslations(
              "opponent_loading_title",
              "Loading opponent team"
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="si-oppositeTeamView">
      <div className="si-oppositeTeamView__header">
        <div className="si-oppositeTeamView__header-lhs">
          {flagImagePath && (
            <div
              className="si-oppositeTeamView__flag"
              style={leagueCardThumbStyle}
            >
              <RaceweekFlag
                size="md"
                countryName={selectedFixture?.CountryName}
                updateFlagSquareImg={updateFlagSquareImg}
              />
            </div>
          )}
          <span className="si-oppositeTeamView__country">
            {selectedFixture?.CountryName || ""}
          </span>
        </div>
        <div className="si-oppositeTeamView__header-rhs">
          <span className="si-oppositeTeamView__round">
            {data.roundNo || ""}
          </span>
        </div>
      </div>
      <div className="si-oppositeTeamView__summary-container">
        <div
          className={`si-oppositeTeamView__summary ${
            !isUserTeam ? "si-oppositeTeamView__summary--non-user" : ""
          }`}
        >
          <div
            className={`si-oppositeTeamView__summary-top ${
              isUserTeam ? "si-oppositeTeamView__summary-top--user" : ""
            }`}
          >
            <div className="si-oppositeTeamView__summary-lhs">
              <TeamSerialNumber teamNumber={data.teamNo || 1} />
              <div className="si-oppositeTeamView__summary-names">
                <div className="si-oppositeTeamView__summary-teamName">
                  {data.teamName || ""}
                </div>
                <div className="si-oppositeTeamView__summary-userName">
                  {data.userName || ""}
                </div>
              </div>
            </div>
          </div>
          <div className="si-oppositeTeamView__summary-bottom">
            <div className="si-oppositeTeamView__summary-item">
              <span className="si-oppositeTeamView__summary-label">
                <i className="f1i-cost-cap"></i>
                {getTranslations("create_team_cost_cap", "Cost cap.")}
              </span>
              <span className="si-oppositeTeamView__summary-value">
                {oppositeTeamDetails?.team_info?.teamBal !== null &&
                oppositeTeamDetails?.team_info?.teamBal !== undefined
                  ? `$${
                      typeof oppositeTeamDetails?.team_info?.teamBal ===
                      "number"
                        ? oppositeTeamDetails?.team_info?.teamBal.toFixed(1)
                        : parseFloat(
                            oppositeTeamDetails?.team_info?.teamBal
                          ).toFixed(1)
                    }M`
                  : "--"}
              </span>
            </div>
            {selectedOpponentBooster?.[0] && (
              <div className="si-oppositeTeamView__summary-item">
                <BoosterChips
                  boosterCls={selectedOpponentBooster[0].boosterCls}
                  boosterIcon={selectedOpponentBooster[0].boosterIcon}
                  boosterText={selectedOpponentBooster[0].boosterText}
                  hideBoosterText={false}
                />
              </div>
            )}
            {!selectedOpponentBooster?.[0] && (
              <div className="si-oppositeTeamView__summary-divider"></div>
            )}
            <div className="si-oppositeTeamView__summary-item">
              <span className="si-oppositeTeamView__summary-label">
                <i className="f1i-star"></i>
                {getTranslations("my_team_points", "Points")}
                {isProvisional && (
                  <span>
                    <ProvisionalPtsTooltip isTooltipTrue={true} />
                  </span>
                )}
              </span>
              <span
                className={`si-oppositeTeamView__summary-value ${(oppositeTeamDetails?.gdpoints || 0) < 0 ? "si-oppositeTeamView__summary-value--negative" : ""}`}
              >
                {formatPoints(oppositeTeamDetails?.gdpoints || 0)}{" "}
                <span className="si-oppositeTeamView__summary-pts">
                  {getTranslations("my_team_points_short", "PTS")}
                </span>
              </span>
            </div>
          </div>
          <div className="si-oppositeTeamView__notice">
            {!!oppositeTeamDetails?.islateonboard && (
              <div className="si-errMsg__wrap si-errMsg__wrap--lob">
                <Notes
                  customCls={"si-error "}
                  iconName={"f1i-error"}
                  text={getTranslations(
                    "completed_races_team_late_onboard_message",
                    "*Late team creation incurs a 10 point deduction. Points will be calculated from the date of creation."
                  )}
                />
              </div>
            )}
            {!!oppositeTeamDetails?.inactive_driver_penality_points && (
              <div className="si-errMsg__wrap si-errMsg__wrap--inactivepenpts">
                <Notes
                  customCls={"si-error "}
                  iconName={"f1i-error"}
                  text={getTranslations(
                    "completed_races_team_inactive_driver_swap_message",
                    "Inactive driver penalty points = {INACTIVE_PENALTIES} pts"
                  ).replace(
                    "{INACTIVE_PENALTIES}",
                    oppositeTeamDetails?.inactive_driver_penality_points
                  )}
                />
              </div>
            )}
            {!!oppositeTeamDetails?.usersubs &&
              !!oppositeTeamDetails?.team_info?.subsallowed &&
              oppositeTeamDetails?.usersubs >
                oppositeTeamDetails?.team_info?.subsallowed && (
                <div className="si-errMsg__wrap si-errMsg__wrap--inactivepenpts">
                  <Notes
                    customCls={"si-error "}
                    iconName={"f1i-error"}
                    text={
                      getTranslations(
                        "completed_races_team_additonal_transfer_message",
                        "*-4 points for each additional transfers. (-4x{{ADDITIONAL_TRANSFERS}} = -{{ADDITIONAL_TRANSFER_COST}} pts)"
                      )
                        .replace(
                          "{{ADDITIONAL_TRANSFERS}}",
                          oppositeTeamDetails?.usersubs -
                            oppositeTeamDetails?.team_info?.subsallowed
                        ) // TODO: get from API
                        .replace(
                          "{{ADDITIONAL_TRANSFER_COST}}",
                          oppositeTeamDetails?.extrasubscost *
                            (oppositeTeamDetails?.usersubs -
                              oppositeTeamDetails?.team_info?.subsallowed)
                        ) // TODO: get from API
                    }
                  />
                </div>
              )}
          </div>
        </div>

        <div className="si-oppositeTeamView__roster">
          {drivers?.map((player, index) => {
            const isInactive =
              player?.IsActive === 0 || player?.IsActive === "0";
            const isReplaced = player?.isReplaced || false;
            const isFinalFixReplaced = player?.isFinalFixReplaced || false;
            const isSwapped = player?.isSwapped || false;

            return (
              <div
                key={index}
                className={`si-oppositeTeamView__roster-item ${
                  isReplaced ? "si-oppositeTeamView__roster-item--replaced" : ""
                } ${
                  isInactive ? "si-oppositeTeamView__roster-item--inactive" : ""
                }`}
              >
                <PlayerImageWithChips player={player} />
                <div className="si-oppositeTeamView__roster-info">
                  <div className="si-oppositeTeamView__roster-name">
                    {player.FUllName &&
                      (() => {
                        const nameParts = splitName(player.FUllName);
                        return (
                          <>
                            {nameParts.firstName && (
                              <span className="si-oppositeTeamView__roster-name-first">
                                {nameParts.firstName}
                              </span>
                            )}
                            {nameParts.lastName && (
                              <span className="si-oppositeTeamView__roster-name-last">
                                {nameParts.lastName}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    {player.hasVideo && (
                      <i className="f1i-youtube si-oppositeTeamView__roster-video"></i>
                    )}
                  </div>
                  {isFinalFixReplaced && (
                    <div className="si-oppositeTeamView__roster-replaced">
                      {getTranslations("player_replaced", "Replaced")}
                    </div>
                  )}
                  {isSwapped && (
                    <div className="si-oppositeTeamView__roster-replaced">
                      {getTranslations("player_swapped_text", "Swapped")}
                    </div>
                  )}
                </div>
                <div
                  className={`si-oppositeTeamView__roster-points ${
                    player.points < 0
                      ? "si-oppositeTeamView__roster-points--negative"
                      : ""
                  }`}
                >
                  <span className="si-oppositeTeamView__roster-points-value">
                    {formatPoints(
                      playerPoints({ player, team: oppositeTeamDetails })
                    )}
                  </span>
                  <span className="si-oppositeTeamView__roster-points-label">
                    {getTranslations(
                      "my_team_points_short",
                      "PTS"
                    ).toUpperCase()}
                  </span>
                </div>
                <div
                  className="si-oppositeTeamView__roster-arrow"
                  onClick={() => handlePlayerClick(player)}
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePlayerClick(player);
                    }
                  }}
                  aria-label={`View details for ${player.FUllName || "player"}`}
                >
                  <i className="f1i-chevron-right"></i>
                </div>
              </div>
            );
          })}
        </div>
        <div className="si-oppositeTeamView__roster">
          {constructors?.map((player, index) => {
            const isInactive =
              player?.IsActive === 0 || player?.IsActive === "0";
            const isReplaced = player?.isReplaced || false;
            const isFinalFixReplaced = player?.isFinalFixReplaced || false;
            const isSwapped = player?.isSwapped || false;

            return (
              <div
                key={index}
                className={`si-oppositeTeamView__roster-item ${
                  isReplaced ? "si-oppositeTeamView__roster-item--replaced" : ""
                } ${
                  isInactive ? "si-oppositeTeamView__roster-item--inactive" : ""
                }`}
              >
                <PlayerImageWithChips player={player} />
                <div className="si-oppositeTeamView__roster-info">
                  <div className="si-oppositeTeamView__roster-name si-oppositeTeamView__roster-name--constructor">
                    {player.FUllName}
                    {player.hasVideo && (
                      <i className="f1i-youtube si-oppositeTeamView__roster-video"></i>
                    )}
                  </div>
                  {isFinalFixReplaced && (
                    <div className="si-oppositeTeamView__roster-replaced">
                      {getTranslations("player_replaced", "Replaced")}
                    </div>
                  )}
                  {isSwapped && (
                    <div className="si-oppositeTeamView__roster-replaced">
                      {getTranslations("player_swapped_text", "Swapped")}
                    </div>
                  )}
                </div>
                <div
                  className={`si-oppositeTeamView__roster-points ${
                    player.points < 0
                      ? "si-oppositeTeamView__roster-points--negative"
                      : ""
                  }`}
                >
                  <span className="si-oppositeTeamView__roster-points-value">
                    {formatPoints(
                      playerPoints({ player, team: oppositeTeamDetails })
                    )}
                  </span>
                  <span className="si-oppositeTeamView__roster-points-label">
                    {getTranslations(
                      "my_team_points_short",
                      "PTS"
                    ).toUpperCase()}
                  </span>
                </div>
                <div
                  className="si-oppositeTeamView__roster-arrow"
                  onClick={() => handlePlayerClick(player)}
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePlayerClick(player);
                    }
                  }}
                  aria-label={`View details for ${player.FUllName || "constructor"}`}
                >
                  <i className="f1i-chevron-right"></i>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <PlayerInfo
        hasCTAs={false}
        isOppositeTeam={true}
        selectedGameday={selectedOption}
      />
    </div>
  );
};

export default OppositeTeamView;
